import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, HardHat, ExternalLink, MapPin, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../../shared/navigation/returnTo';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import { Tile } from '../../../shared/components/ui/primitives';
import {
  certifyContractorBill,
  createContractorBill,
  createContractorEngagement,
  endContractorEngagement,
  getContractorBills,
  getContractorEngagements,
  getContractorSettlement,
  rejectContractorBill,
  verifyContractorBill
} from '../../services/api/implementation/impl';

/**
 * Dues rolled up per contractor, plus in-house labour as its own line.
 *
 * In-house appears here rather than being filtered out because the question
 * this screen answers is "what does the company owe for labour", and workmen
 * engaged directly are part of that answer even though there is no contractor
 * to settle with.
 */
const ContractorLedger = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isFetching } = useQuery({
    queryKey: ['contractorSettlement'],
    queryFn: getContractorSettlement
  });
  const bills = useQuery({
    queryKey: ['contractorBills'],
    queryFn: () => getContractorBills()
  });
  const engagements = useQuery({
    queryKey: ['contractorEngagements'],
    queryFn: () => getContractorEngagements({ active: 'true' })
  });

  const rows = data?.data ?? [];
  // The tiles were split but the table was not: one column headed "Contractor"
  // held an "In-house" row, which is the thing the settlement screen must never
  // blur. Contractors are settled against a bill; the builder's own workmen are
  // paid per person on Wages. Two lists, because they are two answers.
  const contractorRows = rows.filter((r) => r.sp_id != null);
  const inHouseRows = rows.filter((r) => r.sp_id == null);
  // Split, because the two are settled in different places. One number here
  // made wages owed to directly-engaged workmen read as money owed to a
  // contractor — and on a builder with no contractors at all, the whole
  // headline was somebody else's.
  const contractorDue = data?.meta?.contractor_due ?? 0;
  const inHouseDue = data?.meta?.in_house_due ?? 0;
  const billRows = bills.data?.data ?? [];
  const engagementRows = engagements.data?.data ?? [];

  // A contractor sees the sites he is engaged on. Showing the builder that
  // list, here beside what the man is owed, is the only place it is obvious
  // that an unengaged contractor can see nothing.
  const sitesBySp = engagementRows.reduce((acc, row) => {
    const list = acc[row.sp_id] ?? [];
    list.push(row);
    acc[row.sp_id] = list;
    return acc;
  }, {});

  const openContractor = (spId) =>
    navigateWithReturn(navigate, `/builder/serviceprovider/${spId}`, {
      returnTo: buildReturnToFromLocation(location)
    });

  const engage = useMutation({
    mutationFn: createContractorEngagement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractorEngagements'] });
      toast.success('Engaged');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not engage that contractor')
  });

  const endEngagement = useMutation({
    mutationFn: (id) => endContractorEngagement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractorEngagements'] });
      toast.success('Engagement ended');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not end that engagement')
  });

  // One id at a time, so only the row being acted on greys out rather than the
  // whole table going dead while a single bill is verified.
  const [busyBillId, setBusyBillId] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const refreshBills = () => {
    queryClient.invalidateQueries({ queryKey: ['contractorBills'] });
    queryClient.invalidateQueries({ queryKey: ['contractorSettlement'] });
  };

  const verify = useMutation({
    mutationFn: (id) => verifyContractorBill(id, {}),
    onMutate: (id) => setBusyBillId(id),
    onSettled: () => setBusyBillId(null),
    onSuccess: () => {
      refreshBills();
      toast.success('Work verified');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not verify that bill')
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }) => rejectContractorBill(id, { reason }),
    onSuccess: () => {
      refreshBills();
      setRejecting(null);
      toast.success('Sent back to the contractor');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not send that back')
  });

  const certify = useMutation({
    mutationFn: (id) => certifyContractorBill(id),
    onMutate: (id) => setBusyBillId(id),
    onSettled: () => setBusyBillId(null),
    onSuccess: () => {
      refreshBills();
      toast.success('Certified');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not certify')
  });

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Contractor Settlement' }} />

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
          <Tile variant="card"
            label="Owed to contractors"
            value={formatMoney(contractorDue)}
            hint="Settled here, against a bill."
            icon={<Building2 size={18} />}
            tone="info"
          />

          {/* Shown whenever there are workmen engaged directly, including at
              zero. A tile that disappears when the balance clears reads as
              "this builder has no in-house labour", which is a different and
              wrong statement. */}
          {inHouseRows.length > 0 && (
            <Tile variant="card"
              label="Owed to your own workmen"
              value={formatMoney(inHouseDue)}
              hint="Paid per person on Wages, not settled here."
              icon={<HardHat size={18} />}
              tone="success"
            />
          )}
        </div>

        {isFetching ? (
          <Loader />
        ) : (
          <>
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-800">Contractors</h2>
            <p className="text-xs text-gray-500">
              Settled here, against a certified bill.
            </p>
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <Th>Contractor</Th>
                  <Th>Workmen</Th>
                  <Th>Sites engaged</Th>
                  <Th>Outstanding</Th>
                  <Th>Paid to date</Th>
                </tr>
              </thead>
              <tbody>
                {contractorRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-gray-500">
                      No contractors engaged yet.
                    </td>
                  </tr>
                )}
                {contractorRows.map((row) => (
                  <tr key={row.sp_id} className="border-t border-gray-100">
                    <Td>
                      <button
                        type="button"
                        onClick={() => openContractor(row.sp_id)}
                        className="group inline-flex items-center gap-1.5 font-medium text-gray-900 hover:text-brand-700"
                      >
                        {row.name}
                        <ExternalLink
                          size={13}
                          className="opacity-0 group-hover:opacity-100"
                        />
                      </button>
                    </Td>
                    <Td>{row.workers}</Td>
                    <Td>
                      <SiteList
                        sites={sitesBySp[row.sp_id] ?? []}
                        loading={engagements.isFetching}
                        onEnd={(id) => endEngagement.mutate(id)}
                      />
                    </Td>
                    <Td className={row.due < 0 ? 'text-warning-fg' : 'text-gray-900'}>
                      {formatMoney(row.due)}
                      {row.due < 0 && (
                        <div className="text-xs text-warning-fg">advances outstanding</div>
                      )}
                    </Td>
                    <Td className="text-gray-500">{formatMoney(row.paid)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <EngageOnSite
            contractors={contractorRows}
            busy={engage.isPending}
            onEngage={(payload) => engage.mutate(payload)}
          />
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-800">Engaged directly</h2>
            <p className="text-xs text-gray-500">
              The builder&apos;s own workmen. Paid per person on Wages — there is
              no bill to certify, which is why they are not in the list above.
            </p>
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <Th>Engaged by</Th>
                    <Th>Workmen</Th>
                    <Th>Outstanding</Th>
                    <Th>Paid to date</Th>
                  </tr>
                </thead>
                <tbody>
                  {inHouseRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-gray-500">
                        Nobody engaged directly.
                      </td>
                    </tr>
                  )}
                  {inHouseRows.map((row) => (
                    <tr key="in_house" className="border-t border-gray-100">
                      <Td>
                        <div className="font-medium text-gray-900">{row.name}</div>
                      </Td>
                      <Td>{row.workers}</Td>
                      <Td className={row.due < 0 ? 'text-warning-fg' : 'text-gray-900'}>
                        {formatMoney(row.due)}
                        {row.due < 0 && (
                          <div className="text-xs text-warning-fg">advances outstanding</div>
                        )}
                      </Td>
                      <Td className="text-gray-500">{formatMoney(row.paid)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          </>
        )}

        {rejecting && (
          <RejectBill
            bill={rejecting}
            busy={reject.isPending}
            onClose={() => setRejecting(null)}
            onConfirm={(reason) =>
              reject.mutate({ id: rejecting.id, reason })
            }
          />
        )}

        <RaBills
          contractors={contractorRows}
          bills={billRows}
          loading={bills.isFetching}
          busyId={busyBillId}
          onCertify={(id) => certify.mutate(id)}
          onVerify={(id) => verify.mutate(id)}
          onReject={(bill) => setRejecting(bill)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['contractorBills'] })}
        />
      </div>
    </Layout>
  );
};

/**
 * The sites one contractor can see.
 *
 * Worth showing beside what he is owed, because this list is also his
 * permissions: a contractor with no sites here sees no projects at all and
 * cannot place a workman. That used to be invisible until somebody rang up.
 */
const SiteList = ({ sites, loading, onEnd }) => {
  if (loading) return <span className="text-xs text-gray-400">…</span>;
  if (sites.length === 0) {
    return (
      <span className="text-xs text-warning-fg">
        No sites — he cannot place anyone
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {sites.map((site) => (
        <span
          key={site.id}
          className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700"
        >
          {site.project?.name ?? `Project #${site.project_id}`}
          <button
            type="button"
            aria-label={`End on ${site.project?.name ?? site.project_id}`}
            onClick={() => onEnd(site.id)}
            className="text-gray-400 hover:text-danger-fg"
          >
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  );
};

/**
 * Put a contractor on a site before he has anybody there.
 *
 * The chicken and egg this solves: visibility used to be worked out from the
 * workmen he already had on a site, so a contractor starting fresh could see
 * nothing and place nobody, and builder staff had to add his first man for
 * him.
 */
const EngageOnSite = ({ contractors, busy, onEngage }) => {
  const { projectsForDropdown } = useProjectsList();
  const [spId, setSpId] = useState('');
  const [projectId, setProjectId] = useState('');

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-gray-300 p-3">
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <MapPin size={14} />
        Engage on a site
      </div>
      <select
        value={spId}
        onChange={(e) => setSpId(e.target.value)}
        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm min-w-[170px]"
      >
        <option value="">Contractor</option>
        {contractors.map((c) => (
          <option key={c.sp_id} value={c.sp_id}>
            {c.name}
          </option>
        ))}
      </select>
      <select
        value={projectId}
        onChange={(e) => setProjectId(e.target.value)}
        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm min-w-[170px]"
      >
        <option value="">Site</option>
        {(projectsForDropdown ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!spId || !projectId || busy}
        onClick={() => {
          onEngage({ sp_id: Number(spId), project_id: Number(projectId) });
          setProjectId('');
        }}
        className="rounded bg-brand-700 px-3 py-2 text-sm text-white disabled:opacity-50"
      >
        Engage
      </button>
      <p className="w-full text-[11px] text-gray-500">
        This is what he can see. A contractor engaged on nothing has no sites
        in his own app, and cannot add a workman to one.
      </p>
    </div>
  );
};

const emptyLine = () => ({ description: '', unit: '', quantity: '', rate: '' });

const RaBills = ({
  contractors,
  bills,
  loading,
  busyId,
  onCertify,
  onVerify,
  onReject,
  onCreated
}) => {
  const [spId, setSpId] = useState('');
  const [amount, setAmount] = useState('');
  const [billNo, setBillNo] = useState('');
  // Lines are optional. A first running account before measurement has caught
  // up is legitimately one figure, and forcing lines on it would only produce
  // one line called "work done".
  const [lines, setLines] = useState([]);
  const [retentionPercent, setRetentionPercent] = useState('');
  const [tdsPercent, setTdsPercent] = useState('');
  const [otherDeductions, setOtherDeductions] = useState('');

  const totals = billPreview({
    amount,
    lines,
    retentionPercent,
    tdsPercent,
    otherDeductions
  });

  /** A setState updater that changes one field of one line. */
  const editLine = (index, key, value) => (prev) =>
    prev.map((line, i) => (i === index ? { ...line, [key]: value } : line));

  const reset = () => {
    setAmount('');
    setBillNo('');
    setLines([]);
    setRetentionPercent('');
    setTdsPercent('');
    setOtherDeductions('');
  };

  const create = useMutation({
    mutationFn: createContractorBill,
    onSuccess: () => {
      reset();
      onCreated();
      toast.success('Bill drafted');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not draft the bill')
  });

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-gray-800">Running-account bills</h2>
      <p className="text-xs text-gray-500">
        A payment can be recorded against a bill, so what a contractor is
        settled for is a certified amount rather than an unnamed pile of dues.
      </p>
      <div className="flex flex-wrap gap-2 items-end">
        <select
          value={spId}
          onChange={(e) => setSpId(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm min-w-[180px]"
        >
          <option value="">Contractor</option>
          {contractors.map((c) => (
            <option key={c.sp_id} value={c.sp_id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          placeholder="Bill no"
          value={billNo}
          onChange={(e) => setBillNo(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          type="number"
          placeholder="Gross amount"
          value={amount}
          disabled={lines.length > 0}
          onChange={(e) => setAmount(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-36 disabled:bg-gray-50 disabled:text-gray-500"
          title={
            lines.length > 0
              ? 'The lines add up to the gross'
              : 'The gross value of the work'
          }
        />
        <button
          type="button"
          onClick={() => setLines((prev) => [...prev, emptyLine()])}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
        >
          Add a line
        </button>
      </div>

      {lines.length > 0 && (
        <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
          {lines.map((line, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <input
                placeholder="Description"
                value={line.description}
                onChange={(e) => setLines(editLine(index, 'description', e.target.value))}
                className="flex-1 min-w-[180px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
              />
              <input
                placeholder="Unit"
                value={line.unit}
                onChange={(e) => setLines(editLine(index, 'unit', e.target.value))}
                className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
              />
              <input
                type="number"
                placeholder="Qty"
                value={line.quantity}
                onChange={(e) => setLines(editLine(index, 'quantity', e.target.value))}
                className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
              />
              <input
                type="number"
                placeholder="Rate"
                value={line.rate}
                onChange={(e) => setLines(editLine(index, 'rate', e.target.value))}
                className="w-28 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
              />
              <span className="w-28 text-right text-sm tabular-nums text-gray-700">
                {formatMoney(Number(line.quantity || 0) * Number(line.rate || 0))}
              </span>
              <button
                type="button"
                aria-label="Remove line"
                onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                className="text-gray-400 hover:text-danger-fg"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <Deduction
          label="Retention %"
          value={retentionPercent}
          onChange={setRetentionPercent}
        />
        <Deduction label="TDS %" value={tdsPercent} onChange={setTdsPercent} />
        <Deduction
          label="Other deductions"
          value={otherDeductions}
          onChange={setOtherDeductions}
          wide
        />

        {/* What the contractor is actually paid. The screen used to show only
            the gross, so retention and tax read as money still owed to him. */}
        <div className="ml-auto rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
          <span className="text-gray-500">Gross </span>
          <span className="tabular-nums">{formatMoney(totals.gross)}</span>
          <span className="text-gray-300"> · </span>
          <span className="text-gray-500">less </span>
          <span className="tabular-nums">{formatMoney(totals.deductions)}</span>
          <span className="text-gray-300"> · </span>
          <span className="font-semibold text-gray-900">
            Net {formatMoney(totals.net)}
          </span>
        </div>

        <button
          type="button"
          disabled={!spId || totals.gross <= 0 || totals.net < 0 || create.isPending}
          className="rounded bg-brand-700 px-3 py-2 text-sm text-white disabled:opacity-50"
          onClick={() =>
            create.mutate({
              sp_id: Number(spId),
              bill_no: billNo || undefined,
              certified_amount: lines.length ? undefined : Number(amount),
              lines: lines.length
                ? lines.map((line) => ({
                    description: line.description || undefined,
                    unit: line.unit || undefined,
                    quantity: line.quantity === '' ? undefined : Number(line.quantity),
                    rate: line.rate === '' ? undefined : Number(line.rate)
                  }))
                : undefined,
              retention_percent: retentionPercent === '' ? undefined : Number(retentionPercent),
              tds_percent: tdsPercent === '' ? undefined : Number(tdsPercent),
              other_deductions:
                otherDeductions === '' ? undefined : Number(otherDeductions)
            })
          }
        >
          Draft bill
        </button>
      </div>

      {totals.net < 0 && (
        <p className="text-xs text-danger-fg">
          The deductions come to more than the bill.
        </p>
      )}
      {loading ? (
        <Loader />
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <Th>Bill</Th>
                <Th>Contractor</Th>
                <Th>Status</Th>
                <Th>Gross</Th>
                <Th>Deductions</Th>
                <Th>Net payable</Th>
                <Th>Paid</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {bills.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    No RA-bills yet.
                  </td>
                </tr>
              )}
              {bills.map((b) => (
                <tr key={b.id} className="border-t border-gray-100">
                  <Td>{b.bill_no || `#${b.id}`}</Td>
                  <Td>{b.contractor_name || b.sp?.company_name || `Contractor ${b.sp_id}`}</Td>
                  <Td>
                    <BillStatus bill={b} />
                  </Td>
                  <Td className="tabular-nums">{formatMoney(b.certified_amount)}</Td>
                  <Td className="tabular-nums text-gray-500">
                    {formatMoney(deductionsOf(b))}
                    {(b.lines ?? []).length > 0 && (
                      <div className="text-xs text-gray-400">
                        {b.lines.length} line{b.lines.length === 1 ? '' : 's'}
                      </div>
                    )}
                  </Td>
                  <Td className="tabular-nums font-medium text-gray-900">
                    {formatMoney(b.net_payable ?? b.certified_amount)}
                  </Td>
                  <Td className="tabular-nums">{formatMoney(b.paid)}</Td>
                  <Td>
                    <BillActions
                      bill={b}
                      busy={busyId === b.id}
                      onVerify={() => onVerify(b.id)}
                      onReject={() => onReject(b)}
                      onCertify={() => onCertify(b.id)}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * Sending a bill back.
 *
 * The reason is required, and the backend refuses without one: a rejection
 * with no reason is an argument by silence, and the contractor's only move is
 * to resubmit the same thing and hope.
 */
const RejectBill = ({ bill, busy, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  return (
    <Modal name={`Send back ${bill.bill_no || `#${bill.id}`}`} onClose={onClose} width={40}>
      <div className="space-y-4 p-1">
        <p className="text-sm text-gray-600">
          It goes back to the contractor as a draft, under the same number, and
          he can correct it and submit again.
        </p>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">
            What is wrong with it?
          </span>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Quantities on line 2 do not match what was measured"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!reason.trim() || busy}
            onClick={() => onConfirm(reason.trim())}
            className="rounded-lg bg-danger px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send back'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Where a bill has got to, and who it is waiting on.
 *
 * The status alone answers the wrong question. "SUBMITTED" tells a builder
 * nothing he wants to know; "waiting on the site" tells him whose desk it is
 * sitting on, which is the only reason anybody opens this screen.
 */
const BILL_STATES = {
  DRAFT: ['Draft', 'bg-gray-100 text-gray-600', 'with the contractor'],
  SUBMITTED: ['Submitted', 'bg-warning-bg text-warning-fg', 'waiting on the site'],
  VERIFIED: ['Verified', 'bg-info-bg text-info-fg', 'waiting to be certified'],
  REJECTED: ['Sent back', 'bg-danger-bg text-danger-fg', 'with the contractor'],
  CERTIFIED: ['Certified', 'bg-brand-50 text-brand-800', 'waiting on payment'],
  SETTLED: ['Settled', 'bg-gray-100 text-gray-500', 'paid in full']
};

const BillStatus = ({ bill }) => {
  const [label, tone, waiting] =
    BILL_STATES[bill.status] ?? [bill.status, 'bg-gray-100 text-gray-600', ''];
  return (
    <div className="space-y-1">
      <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${tone}`}>
        {label}
      </span>
      {waiting && <div className="text-[11px] text-gray-500">{waiting}</div>}
      {bill.status === 'REJECTED' && bill.rejection_reason && (
        <div className="max-w-[16rem] text-[11px] text-danger-fg" title={bill.rejection_reason}>
          {bill.rejection_reason}
        </div>
      )}
    </div>
  );
};

/**
 * What the site can do to this bill right now.
 *
 * Only ever one or two things, and only the ones the backend would actually
 * allow — offering Certify on a bill nobody has verified is a button whose
 * only outcome is an error message.
 */
const BillActions = ({ bill, busy, onVerify, onReject, onCertify }) => {
  const actions = [];
  if (bill.status === 'SUBMITTED') {
    actions.push(['Verify work', onVerify, 'text-brand-700']);
    actions.push(['Send back', onReject, 'text-danger-fg']);
  }
  if (bill.status === 'VERIFIED') {
    actions.push(['Certify', onCertify, 'text-brand-700']);
    actions.push(['Send back', onReject, 'text-danger-fg']);
  }

  if (!actions.length) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {actions.map(([label, onClick, tone]) => (
        <button
          key={label}
          type="button"
          disabled={busy}
          onClick={onClick}
          className={`text-sm hover:underline disabled:opacity-50 ${tone}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

/** What comes off a stored bill before the contractor is paid. */
function deductionsOf(bill) {
  return (
    Number(bill?.retention_amount ?? 0) +
    Number(bill?.tds_amount ?? 0) +
    Number(bill?.other_deductions ?? 0)
  );
}

/**
 * The same arithmetic the backend does, run while somebody types.
 *
 * Deliberately a preview and nothing more: the figures that get stored are the
 * ones the server works out, so a mistake here cannot become a wrong bill.
 */
function billPreview({ amount, lines, retentionPercent, tdsPercent, otherDeductions }) {
  const gross = lines.length
    ? lines.reduce(
        (sum, line) => sum + Number(line.quantity || 0) * Number(line.rate || 0),
        0
      )
    : Number(amount || 0);
  const retention = (gross * Number(retentionPercent || 0)) / 100;
  const tds = (gross * Number(tdsPercent || 0)) / 100;
  const other = Number(otherDeductions || 0);
  const deductions = retention + tds + other;
  return { gross, deductions, net: gross - deductions };
}

const Deduction = ({ label, value, onChange, wide = false }) => (
  <label className="text-xs text-gray-600">
    <span className="mb-1 block">{label}</span>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-lg border border-gray-300 px-3 py-2 text-sm ${
        wide ? 'w-36' : 'w-24'
      }`}
    />
  </label>
);

function formatMoney(value) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return '₹0';
  return `₹${number.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

export default ContractorLedger;
